import React, { useState, useRef, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Alert,
  App,
  Statistic,
  InputRef,
  Checkbox,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  ShopOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MailOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  registerWithOrganization,
  registerWithOrganizationGoogle,
  loginWithGoogle,
  clearError,
  GoogleAuthError,
} from '../../store/slices/authSlice';
import { RegisterWithOrganizationRequest, GoogleProfile, AUTH_ERROR_CODES } from '../../types/auth';
import { authApi } from '../../services/authApi';
import { isGoogleAuthEnabled } from '../../config/google';
import GoogleSignInButton from './GoogleSignInButton';
import LegalDocumentModal from './LegalDocumentModal';
import {
  TERMS_VERSION,
  PRIVACY_VERSION,
  termsContent,
  privacyContent,
} from '../../content/legal';
import './auth-theme.css';
import styles from './RegisterForm.module.css';

const { Countdown } = Statistic;

// Soporte por WhatsApp — mismo número usado en la landing-page (App.tsx:340)
const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

const STEP_LABELS = ['Personal', 'Email', 'Negocio', 'Confirmar'] as const;

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
}

const WhatsAppIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const Stepper: React.FC<{ current: number }> = ({ current }) => (
  <div className={styles.stepper}>
    {STEP_LABELS.map((label, i) => {
      const cls = [
        styles.step,
        i === current ? styles.stepActive : '',
        i < current ? styles.stepDone : '',
      ]
        .filter(Boolean)
        .join(' ');
      return (
        <div key={label} className={cls}>
          <div className={styles.stepDot} aria-current={i === current ? 'step' : undefined}>
            {i < current ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              i + 1
            )}
          </div>
          <div className={styles.stepLabel}>{label}</div>
        </div>
      );
    })}
  </div>
);

/** Índice del paso de datos del negocio, que es donde entra el alta con Google. */
const BUSINESS_STEP = 2;

/** Lo que deja la pantalla de login cuando el correo de Google no tenía cuenta. */
interface GoogleHandoffState {
  googleCredential?: string;
  googleProfile?: GoogleProfile | null;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error: reduxError } = useAppSelector((state) => state.auth);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [localError, setLocalError] = useState<string | null>(null);

  // Traspaso desde el login: si allá se intentó entrar con un correo sin cuenta, aquí
  // llega el mismo ID token para no obligar a pasar por Google dos veces.
  const handoff = (location.state as GoogleHandoffState | null) ?? null;
  const [googleCredential, setGoogleCredential] = useState<string | null>(
    handoff?.googleCredential ?? null,
  );
  const [googleProfile, setGoogleProfile] = useState<GoogleProfile | null>(
    handoff?.googleProfile ?? null,
  );
  const [googleAvailable, setGoogleAvailable] = useState(isGoogleAuthEnabled());

  // Con Google no hay contraseña que pedir ni correo que verificar, así que se arranca
  // directo en los datos del negocio.
  const [currentStep, setCurrentStep] = useState(handoff?.googleCredential ? BUSINESS_STEP : 0);

  // Legal documents modal state
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  // Email verification state — con Google ya viene verificado por Google
  const [emailVerified, setEmailVerified] = useState(!!handoff?.googleCredential);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeExpiry, setCodeExpiry] = useState<number | null>(null);
  const [canResend, setCanResend] = useState(true);
  const inputRefs = useRef<(InputRef | null)[]>([]);

  // Combine redux error with local error for display (local takes priority)
  const error = localError || reduxError;

  // Clear error only when component mounts
  useEffect(() => {
    dispatch(clearError());
    setLocalError(null);
  }, [dispatch]);

  // El perfil de Google llena los campos personales aunque su paso no se muestre: el
  // resumen del último paso los lee del form, y sin esto saldría vacío.
  useEffect(() => {
    if (!googleProfile) return;
    form.setFieldsValue({
      firstName: googleProfile.firstName,
      lastName: googleProfile.lastName,
      email: googleProfile.email,
    });
  }, [googleProfile, form]);

  // Handle sending verification code
  const handleSendVerificationCode = async () => {
    try {
      const email = form.getFieldValue('email');
      if (!email) {
        message.error('Por favor ingresa tu email primero.');
        return;
      }

      setSendingCode(true);
      setLocalError(null);

      // Get reCAPTCHA token if available, otherwise use placeholder for development
      let recaptchaToken = 'development-mode-no-recaptcha';
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('send_verification_code');
        } catch (err) {
          // silently ignore
        }
      }

      await authApi.sendVerificationCode({ email, recaptchaToken });

      message.success('Código de verificación enviado a tu correo.');
      setCodeExpiry(Date.now() + 10 * 60 * 1000); // 10 minutes from now
      setCanResend(false);
      setVerificationCode(['', '', '', '', '', '']);

      // Allow resend after 60 seconds
      setTimeout(() => setCanResend(true), 60000);

      // Focus first input
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      console.error('Error sending verification code:', err);
      const errorMsg =
        err?.response?.data?.message || 'Error al enviar el código de verificación.';
      setLocalError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSendingCode(false);
    }
  };

  // Handle verification code input
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all 6 digits are entered
    if (newCode.every((d) => d !== '') && newCode.join('').length === 6) {
      verifyCode(newCode.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setVerificationCode(newCode);
      verifyCode(pastedData);
    }
  };

  // Verify the code
  const verifyCode = async (code: string) => {
    try {
      const email = form.getFieldValue('email');
      setVerifyingCode(true);
      setLocalError(null);

      const result = await authApi.verifyCode({ email, code });

      if (result.verified) {
        message.success('Email verificado correctamente!');
        setEmailVerified(true);
        setCurrentStep(2); // Move to business data step
      }
    } catch (err: any) {
      console.error('Error verifying code:', err);
      const errorMsg = err?.response?.data?.message || 'Código incorrecto.';
      setLocalError(errorMsg);
      message.error(errorMsg);
      setVerificationCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifyingCode(false);
    }
  };

  /**
   * El botón de Google del registro pasa PRIMERO por el login. Suena al revés, pero es
   * lo que hace que quien ya tiene cuenta y aterrizó aquí por error entre en vez de
   * chocar con "ya existe una cuenta con ese correo": si el login responde que no hay
   * cuenta, ahí mismo llega el perfil con el que se llena el alta.
   */
  const handleGoogleCredential = async (idToken: string) => {
    setLocalError(null);
    try {
      await dispatch(loginWithGoogle({ idToken })).unwrap();
      navigate('/', { replace: true });
    } catch (err) {
      const googleError = err as GoogleAuthError;

      if (googleError?.code === AUTH_ERROR_CODES.googleNoAccount) {
        setGoogleCredential(idToken);
        setGoogleProfile(googleError.profile ?? null);
        setEmailVerified(true);
        setCurrentStep(BUSINESS_STEP);
        return;
      }

      const errorMsg = googleError?.message || 'No pudimos validar tu cuenta de Google.';
      setLocalError(errorMsg);
      message.error(errorMsg);
    }
  };

  /**
   * Token de reCAPTCHA para las dos altas —con contraseña y con Google—, con reintentos:
   * el script de reCAPTCHA a veces no está listo al primer intento y devuelve vacío.
   *
   * El alta con Google lo necesita igual que la otra: tener cuenta de Google no es una
   * barrera (se crean gratis y en masa) y cada alta levanta una base de datos propia.
   *
   * El marcador de desarrollo no es un hueco: el backend solo se salta la validación
   * cuando no hay SecretKey configurada, que es exactamente el caso en que el frontend
   * tampoco tiene SiteKey.
   */
  const getRecaptchaToken = async (): Promise<string> => {
    if (!executeRecaptcha) return 'development-mode-no-recaptcha';

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const token = await executeRecaptcha('register');
        if (token && token.trim() !== '') {
          return token.trim();
        }
      } catch (recaptchaError) {
        console.error(`reCAPTCHA attempt ${attempt} failed:`, recaptchaError);
      }
      if (attempt < 3) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }

    return 'development-mode-no-recaptcha';
  };

  /** Vuelve al alta con correo y contraseña, por si se eligió la cuenta equivocada. */
  const handleDiscardGoogle = () => {
    setGoogleCredential(null);
    setGoogleProfile(null);
    setEmailVerified(false);
    setLocalError(null);
    form.resetFields(['firstName', 'lastName', 'email']);
    setCurrentStep(0);
  };

  const handleGoogleSubmit = async (values: any) => {
    if (!googleCredential) return;

    const allValues = form.getFieldsValue();
    try {
      const recaptchaToken = await getRecaptchaToken();

      await dispatch(
        registerWithOrganizationGoogle({
          idToken: googleCredential,
          recaptchaToken,
          organizationName: allValues.organizationName || values.organizationName,
          restaurantName: allValues.restaurantName || values.restaurantName,
          acceptTermsAndConditions: !!allValues.acceptTermsAndConditions,
          acceptPrivacyNotice: !!allValues.acceptPrivacyNotice,
          termsVersion: TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
        }),
      ).unwrap();

      navigate('/', { replace: true });
    } catch (err) {
      const googleError = err as GoogleAuthError;

      // La cuenta se creó entre medias (otra pestaña, doble envío): entrar es lo que la
      // persona quería de todos modos.
      if (googleError?.code === AUTH_ERROR_CODES.accountExists) {
        try {
          await dispatch(loginWithGoogle({ idToken: googleCredential })).unwrap();
          navigate('/', { replace: true });
          return;
        } catch {
          // Cae al mensaje de abajo.
        }
      }

      const errorMsg = googleError?.message || 'Error en el registro. Por favor intenta de nuevo.';
      setLocalError(errorMsg);
      message.error(errorMsg);
    }
  };

  const handleSubmit = async (values: any) => {
    if (googleCredential) {
      await handleGoogleSubmit(values);
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();

      const allValues = form.getFieldsValue();

      const request: RegisterWithOrganizationRequest = {
        email: allValues.email || values.email,
        password: allValues.password || values.password,
        firstName: allValues.firstName || values.firstName,
        lastName: allValues.lastName || values.lastName,
        organizationName: allValues.organizationName || values.organizationName,
        restaurantName: allValues.restaurantName || values.restaurantName,
        recaptchaToken: recaptchaToken.trim(),
        acceptTermsAndConditions: !!allValues.acceptTermsAndConditions,
        acceptPrivacyNotice: !!allValues.acceptPrivacyNotice,
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
      };

      // Register the user
      await dispatch(registerWithOrganization(request)).unwrap();

      // After successful registration, redirect to dashboard (trial starts immediately)
      navigate('/', { replace: true });
    } catch (err: any) {
      // Error is handled by redux slice, show toast notification for better visibility
      console.error('Registration error in component:', err);
      // Determine error message
      let errorMsg = 'Error en el registro. Por favor intenta de nuevo.';
      if (typeof err === 'string') {
        errorMsg = err;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      // Set local error state for Alert display
      setLocalError(errorMsg);
      // Show error as toast message for immediate visibility
      message.error(errorMsg);
    }
  };

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        await form.validateFields([
          'email',
          'password',
          'confirmPassword',
          'firstName',
          'lastName',
        ]);
        // Move to email verification step
        setCurrentStep(1);
        // Auto-send verification code
        handleSendVerificationCode();
      } else if (currentStep === 2) {
        await form.validateFields(['organizationName', 'restaurantName']);
        setCurrentStep(3);
      }
    } catch (err) {
      // validation errors are shown by AntD
    }
  };

  const handlePrevious = () => {
    // En alta con Google el paso 2 es el primero: "atrás" solo puede significar
    // "elegí mal la cuenta", así que se suelta el token y se vuelve al alta normal.
    if (googleCredential && currentStep === BUSINESS_STEP) {
      handleDiscardGoogle();
      return;
    }

    if (currentStep === 1) {
      // Going back from verification to personal data
      setCurrentStep(0);
      setEmailVerified(false);
      setVerificationCode(['', '', '', '', '', '']);
      setCodeExpiry(null);
    } else if (currentStep === 2 && !emailVerified) {
      // If email not verified yet, go back to verification
      setCurrentStep(1);
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.nav}>
        <a
          className={styles.help}
          href={SUPPORT_WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <WhatsAppIcon />
          ¿Necesitas ayuda?
        </a>
      </header>

      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.logoWrap}>
              <img src="/logo-complete.png" alt="Massrestaurant" className="app-logo" />
            </div>
            <h1 className={styles.title}>Crea tu cuenta</h1>
            <p className={styles.subtitle}>
              Registra tu restaurante o cafetería en Massrestaurant.
            </p>
          </div>

          <Stepper current={currentStep} />

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className={styles.alert}
              onClose={() => {
                dispatch(clearError());
                setLocalError(null);
              }}
            />
          )}

          <Form
            form={form}
            name="register"
            onFinish={handleSubmit}
            layout="vertical"
            requiredMark={false}
            preserve
            className={styles.form}
          >
            {/* Step 0: Personal Data — se DESMONTA en alta con Google, no solo se
                oculta: onFinish valida todos los Form.Item montados, y contraseña y
                confirmación vacías bloquearían el envío con errores en campos que la
                persona no puede ver. Con preserve activo, nombre y correo sobreviven al
                desmontaje y siguen alimentando el resumen. */}
            {!googleCredential && (
            <div className={currentStep === 0 ? styles.section : styles.sectionHidden}>
              <div className={styles.fieldRow}>
                <Form.Item
                  name="firstName"
                  label="Nombre"
                  rules={[
                    { required: true, message: 'Ingresa tu nombre' },
                    { min: 2, message: 'Mínimo 2 caracteres' },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Juan" />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label="Apellido"
                  rules={[
                    { required: true, message: 'Ingresa tu apellido' },
                    { min: 2, message: 'Mínimo 2 caracteres' },
                  ]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Pérez" />
                </Form.Item>
              </div>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Por favor ingresa tu email' },
                  { type: 'email', message: 'Ingresa un email válido' },
                  { max: 100, message: 'El email no puede tener más de 100 caracteres' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="tu@email.com" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  { required: true, message: 'Por favor ingresa tu contraseña' },
                  { min: 8, message: 'La contraseña debe tener al menos 8 caracteres' },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                    message:
                      'Debe incluir mayúscula, minúscula, número y carácter especial (@$!%*?&)',
                  },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirmar contraseña"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Por favor confirma tu contraseña' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Las contraseñas no coinciden'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
              </Form.Item>

              {googleAvailable && (
                <>
                  <div className={styles.divider}>
                    <span>o</span>
                  </div>
                  <GoogleSignInButton
                    text="signup_with"
                    onCredential={handleGoogleCredential}
                    onUnavailable={() => setGoogleAvailable(false)}
                  />
                  <p className={styles.googleHint}>
                    Con Google te saltas la contraseña y el código por correo.
                  </p>
                </>
              )}
            </div>
            )}

            {/* Step 1: Email Verification */}
            <div className={currentStep === 1 ? styles.section : styles.sectionHidden}>
              <div className={styles.verifyIcon} aria-hidden="true">
                <SafetyOutlined />
              </div>
              <h2 className={styles.verifyTitle}>Verifica tu correo</h2>
              <p className={styles.verifySub}>
                Enviamos un código de 6 dígitos a
                <br />
                <strong>{form.getFieldValue('email')}</strong>
              </p>

              {/* 6-digit code input */}
              <div className={styles.codeRow}>
                {verificationCode.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    maxLength={1}
                    disabled={verifyingCode}
                  />
                ))}
              </div>

              {verifyingCode && (
                <div className={styles.timer} style={{ color: 'var(--color-primary)' }}>
                  Verificando código...
                </div>
              )}

              {/* Countdown timer — must be a <div>, not a <p>: AntD Statistic
                  renders block-level HTML and would trigger validateDOMNesting
                  warnings if wrapped in a paragraph. */}
              {codeExpiry && codeExpiry > Date.now() && (
                <div className={styles.timer}>
                  El código expira en{' '}
                  <Countdown
                    value={codeExpiry}
                    format="mm:ss"
                    valueStyle={{ fontSize: 14, color: 'var(--color-primary)' }}
                    onFinish={() => setCodeExpiry(null)}
                  />
                </div>
              )}

              {/* Resend button */}
              <Button
                type="link"
                onClick={handleSendVerificationCode}
                disabled={!canResend || sendingCode}
                loading={sendingCode}
                className={styles.resend}
              >
                {sendingCode
                  ? 'Enviando...'
                  : canResend
                    ? 'Reenviar código'
                    : 'Espera antes de reenviar'}
              </Button>

              <p className={styles.verifyHelp}>
                ¿No recibiste el código? Revisa tu carpeta de spam.
              </p>
            </div>

            {/* Step 2: Business Data */}
            <div className={currentStep === 2 ? styles.section : styles.sectionHidden}>
              <Form.Item
                name="organizationName"
                label="Nombre de la organización"
                rules={[
                  { required: true, message: 'Por favor ingresa el nombre de tu organización' },
                  { min: 3, message: 'El nombre debe tener al menos 3 caracteres' },
                  { max: 100, message: 'El nombre no puede tener más de 100 caracteres' },
                ]}
              >
                <Input prefix={<BankOutlined />} placeholder="Mi Empresa de Restaurantes" />
              </Form.Item>

              <Form.Item
                name="restaurantName"
                label="Nombre del restaurante"
                rules={[
                  { required: true, message: 'Por favor ingresa el nombre de tu restaurante' },
                  { min: 3, message: 'El nombre debe tener al menos 3 caracteres' },
                  { max: 100, message: 'El nombre no puede tener más de 100 caracteres' },
                ]}
              >
                <Input prefix={<ShopOutlined />} placeholder="Restaurante Central" />
              </Form.Item>
            </div>

            {/* Step 3: Confirmation */}
            <div className={currentStep === 3 ? styles.section : styles.sectionHidden}>
              <div className={styles.summaryCard}>
                <h5>Resumen de registro</h5>
                <p>
                  <strong>Nombre:</strong> {form.getFieldValue('firstName')}{' '}
                  {form.getFieldValue('lastName')}
                </p>
                <p>
                  <strong>Email:</strong> {form.getFieldValue('email')}{' '}
                  <span style={{ color: 'var(--color-success-text)', fontSize: 12, fontWeight: 600 }}>
                    {googleCredential ? '(verificado con Google)' : '(verificado)'}
                  </span>
                </p>
                <p>
                  <strong>Organización:</strong> {form.getFieldValue('organizationName')}
                </p>
                <p>
                  <strong>Restaurante:</strong> {form.getFieldValue('restaurantName')}
                </p>
              </div>

              <Alert
                message="Prueba gratuita de 7 días"
                description="Al registrarte, comenzará tu periodo de prueba gratuito de 7 días. No necesitas tarjeta de crédito."
                type="info"
                showIcon
                className={styles.warningAlert}
              />

              <Form.Item
                name="acceptTermsAndConditions"
                valuePropName="checked"
                style={{ marginTop: 4, marginBottom: 8 }}
                className={styles.legalRow}
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error('Debes aceptar los Términos y Condiciones')),
                  },
                ]}
              >
                <Checkbox>
                  He leído y acepto los{' '}
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalOpen(true);
                    }}
                  >
                    Términos y Condiciones
                  </Button>
                </Checkbox>
              </Form.Item>

              <Form.Item
                name="acceptPrivacyNotice"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
                className={styles.legalRow}
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error('Debes aceptar el Aviso de Privacidad')),
                  },
                ]}
              >
                <Checkbox>
                  He leído y acepto el{' '}
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.preventDefault();
                      setPrivacyModalOpen(true);
                    }}
                  >
                    Aviso de Privacidad
                  </Button>
                </Checkbox>
              </Form.Item>
            </div>

            {/* Footer with navigation buttons */}
            <div className={styles.footer}>
              {currentStep > 0 && currentStep !== 1 && (
                <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious}>
                  {googleCredential && currentStep === BUSINESS_STEP ? 'Cambiar cuenta' : 'Anterior'}
                </Button>
              )}

              {currentStep === 1 && (
                <Button icon={<ArrowLeftOutlined />} onClick={handlePrevious}>
                  Cambiar email
                </Button>
              )}

              {currentStep === 0 && (
                <Button
                  type="primary"
                  onClick={handleNext}
                  style={{ marginLeft: 'auto' }}
                  iconPosition="end"
                  icon={<ArrowRightOutlined />}
                >
                  Siguiente
                </Button>
              )}

              {currentStep === 2 && (
                <Button
                  type="primary"
                  onClick={handleNext}
                  style={{ marginLeft: 'auto' }}
                  iconPosition="end"
                  icon={<ArrowRightOutlined />}
                >
                  Siguiente
                </Button>
              )}

              {currentStep === 3 && (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{ marginLeft: 'auto' }}
                >
                  Crear cuenta
                </Button>
              )}
            </div>
          </Form>

          {onSwitchToLogin && (
            <div className={styles.switchWrapper}>
              ¿Ya tienes cuenta?{' '}
              <Button type="link" onClick={onSwitchToLogin}>
                Inicia sesión aquí
              </Button>
            </div>
          )}
        </div>
      </main>

      <footer className={styles.bottom}>
        <span>© {new Date().getFullYear()} Massrestaurant</span>
        <span className={styles.links}>
          <button type="button" onClick={() => setTermsModalOpen(true)}>
            Términos y Condiciones
          </button>
          <button type="button" onClick={() => setPrivacyModalOpen(true)}>
            Aviso de Privacidad
          </button>
        </span>
      </footer>

      <LegalDocumentModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        title="Términos y Condiciones"
        version={TERMS_VERSION}
        content={termsContent}
      />
      <LegalDocumentModal
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        title="Aviso de Privacidad"
        version={PRIVACY_VERSION}
        content={privacyContent}
      />
    </div>
  );
};

export default RegisterForm;
